import type { ProductCategoriesMapping } from "./Product";
import { EmployeeRoles } from "../Employees/EmployeeRoles";

/** Base path appended to REACT_APP_API_URL (gateway may include /api). */
export const POD_CATALOG_BASE = "/v1/products/pod-catalog";

export interface PodCatalogCategory {
  id: number;
  name: string;
}

export interface PodCatalogVariant {
  id: number;
  variant_id: number | null;
  product_id: number;
  color: string | null;
  sku: string | null;
  variant_sku: string | null;
  active: number;
}

export interface PodCatalogProduct {
  id: number;
  product_id: number;
  product_sku: string | null;
  swageazy_selling_price: number | string | null;
  country_of_origin?: string | null;
  production_time?: number | null;
  active: number;
  created_date: Date | string | null;
  modified_date: Date | string | null;
  product_name: string | null;
  categories?: PodCatalogCategory[];
  variants?: PodCatalogVariant[];
  /** Product-level gallery from swageazy_catalogue_images (Swagstore-style). */
  images?: string[];
}

export interface PodCatalogPaginated<T = PodCatalogProduct> {
  page_no: number;
  total_pages: number;
  total_count: number;
  list: T[];
  limit: number;
}

/**
 * GET /pod-product/:productId — v5-like master product fields + POD catalogue fields
 * (no image_urls, vendors, product variants, printing_types, urgent_sourcing on response).
 */
export interface PodCatalogProductDetail {
  id?: number;
  product_id?: number;
  name?: string | null;
  vendor_code?: string | null;
  image_url?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  selling_price?: number | null;
  gst_percentage?: number | null;
  dimensions?: string | null;
  desc?: string | null;
  cost_price?: number | null;
  sku?: string | null;
  hsn_code?: string | null;
  colors?: string | null;
  fast_procurement?: boolean | number | null;
  display?: boolean | number | null;
  zoho_id?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  created_by?: number | null;
  creator_name?: string | null;
  last_modified_by?: number | null;
  modifier_name?: string | null;
  created_date?: string | null;
  modified_date?: string | null;
  active?: boolean | number | null;
  size_chart_url?: string | null;
  categories?: ProductCategoriesMapping[] | PodCatalogCategory[];
  pod_catalogue_id?: number | null;
  pod_catalogue_sku?: string | null;
  swageazy_selling_price?: number | string | null;
  country_of_origin?: string | null;
  production_time?: number | null;
  pod_catalogue_active?: boolean | number | null;
  pod_catalogue_created_date?: string | null;
  pod_catalogue_modified_date?: string | null;
  pod_catalog_variants?: PodCatalogVariant[];
  can_view_pod_catalog_variants?: boolean;
  can_edit_pod_catalog_variants?: boolean;
}

/** Prefer API flags from pod-product GET; fall back to admin role permissions (`all` bypasses via hasPermission). */
export const resolvePodCatalogVariantAccess = (
  detail: PodCatalogProductDetail | null | undefined,
  hasPermission: (role: EmployeeRoles) => boolean
): { canView: boolean; canEdit: boolean } => {
  const canView =
    detail?.can_view_pod_catalog_variants !== undefined
      ? Boolean(detail.can_view_pod_catalog_variants)
      : hasPermission(EmployeeRoles.VIEW_POD_CATALOG_VARIANTS);
  const canEdit =
    detail?.can_edit_pod_catalog_variants !== undefined
      ? Boolean(detail.can_edit_pod_catalog_variants)
      : hasPermission(EmployeeRoles.EDIT_POD_CATALOG_VARIANTS);
  return { canView, canEdit };
};

export type PodCatalogSortBy =
  | "created_date"
  | "modified_date"
  | "product_id"
  | "product_name"
  | "selling_price";

export type PodCatalogSortType = "ASC" | "DESC";

export interface PodCatalogListQuery {
  page_no?: number;
  limit?: number;
  search?: string;
  active?: number;
  product_id?: number;
  sort_by?: PodCatalogSortBy;
  sort_type?: PodCatalogSortType;
  /** Filter list to products mapped to any of these category ids (active mapping). */
  category_ids?: number[];
  min_price?: number;
  max_price?: number;
}

export interface PodCatalogUpsertBody {
  product_sku?: string;
  swageazy_selling_price?: number;
  country_of_origin?: string;
  production_time?: number;
}

export interface PodCatalogVariantUpsert {
  id?: number;
  /** Master product variant id; omit for new POD rows if backend allows. */
  variant_id?: number;
  color?: string;
  sku?: string;
  variant_sku?: string;
}

export interface PodCatalogVariantDrawerRow extends PodCatalogVariantUpsert {
  rowId: string;
  isEditing: boolean;
}

export interface PodCatalogVariantsUpsertBody {
  variants: PodCatalogVariantUpsert[];
}

/** Default POD product SKU: SE-{productId} */
export const defaultPodProductSku = (productId: number): string =>
  `SE-${productId}`;

/** Default POD variant SKU: SE-{productId}-{SIZE}[-{COLOR}] */
export const buildPodVariantSku = (
  productId: number,
  size: string,
  color?: string | null
): string => {
  const normalize = (val: string) =>
    String(val)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "-")
      .replace(/[^A-Z0-9-]/g, "");
  const parts = ["SE", String(productId), normalize(size)];
  if (color?.trim()) parts.push(normalize(color));
  return parts.join("-");
};

export const sanitizePodCatalogVariantsBody = (
  body: PodCatalogVariantsUpsertBody
): PodCatalogVariantsUpsertBody => ({
  variants: body.variants.map(
    ({ id, variant_id, color, sku, variant_sku }) => ({
      ...(id != null ? { id } : {}),
      ...(variant_id != null ? { variant_id } : {}),
      ...(color !== undefined ? { color } : {}),
      ...(sku !== undefined ? { sku } : {}),
      ...(variant_sku !== undefined ? { variant_sku } : {}),
    })
  ),
});

